import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useWorkers } from "context/providers/workers";
import WorkerAvatar from "../WorkerAvatar";
import getIssueType from "helpers/issue_types";
import useCreate from "hooks/useCreate";
import ModalDelete from "components/ModalDelete";

const ManageWorkers = ({ data, refetch }) => {
  const { workers_list_options } = useWorkers();
  const [is_open, setOpen] = useState(null);
  const [worker_id, setWorkerId] = useState(data?.worker?.id || null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    if (is_open) {
      const sub_issues_without_worker = [
        data?.worker?.id ? null : data?.id,
        ...data?.sub_issues
          .filter((item) => !item?.worker?.id)
          .map((item) => item.id),
      ].filter(Boolean);
      setSelectedRowKeys(sub_issues_without_worker);
    } else {
      setSelectedRowKeys([]);
      setWorkerId(null);
    }
    // eslint-disable-next-line
  }, [is_open]);

  const { is_saving: is_saving_unassign, create: unassignWorker } = useCreate({
    path: `/issues/${data?.id}/unassign_from_worker`,
    onSuccess: refetch,
    success_message: "Zgłoszenie zostałe odpisane od pracownika",
  });

  const { is_saving: is_saving_bulk_assign, create: bulkAssignWorker } =
    useCreate({
      path: "/issues/bulk_assign",
      onSuccess: () => {
        setSelectedRowKeys([]);
        refetch();
      },
      success_message: "Zgłoszenia zostały przypisane do pracownika",
    });

  const { is_saving: is_saving_bulk_unassign, create: bulkUnAssignWorker } =
    useCreate({
      path: "/issues/bulk_unassign",
      onSuccess: () => {
        setSelectedRowKeys([]);
        refetch();
      },
      success_message: "Zgłoszenia zostały odpisane od pracownika",
    });

  const onSelectChange = (newSelectedRowKeys) =>
    setSelectedRowKeys(newSelectedRowKeys);

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: null,
    columnWidth: 48,
  };

  const sub_issues = useMemo(() => {
    const master_issue = {
      id: data?.id,
      key: data?.id,
      client_street: data?.client_street,
      client_building_number: data?.client_building_number,
      client_apartment_number: data?.client_apartment_number,
      worker: data?.worker,
      issue_type: "master",
    };
    if (data?.sub_issues?.length > 0) {
      return [
        master_issue,
        ...data.sub_issues.map((item) => ({
          ...item,
          key: item.id,
          issue_type: "subtask",
        })),
      ];
    }
    return [master_issue];
  }, [data]);

  const columns = [
    {
      title: "Typ zgłoszenia",
      render: (_, { issue_type }) => {
        const type = getIssueType(issue_type);
        return <Tag color={type.color}>{type.label}</Tag>;
      },
    },
    {
      title: "Ulica",
      dataIndex: "client_street",
    },
    {
      title: "Numer budynku",
      dataIndex: "client_building_number",
    },
    {
      title: "Numer mieszkania",
      dataIndex: "client_apartment_number",
    },
    {
      title: "Pracownik",
      dataIndex: "worker",
      width: 220,
      render: (_, record) => (
        <div style={{ textAlign: "center" }}>
          {record.worker?.id ? <WorkerAvatar worker={record.worker} /> : "-"}
        </div>
      ),
    },
  ];

  const handleCancel = () => setOpen(false);

  const handleAssign = () =>
    bulkAssignWorker({ ids: selectedRowKeys, worker_id });

  const handleUnassign = () => bulkUnAssignWorker({ ids: selectedRowKeys });

  const can_unassign = useMemo(() => {
    if (sub_issues.length > 0 && selectedRowKeys.length > 0) {
      return sub_issues
        .filter(({ id }) => selectedRowKeys.includes(id))
        .every(({ worker }) => worker?.id);
    }
    return true;
  }, [sub_issues, selectedRowKeys]);

  return (
    <Card
      style={{ marginBottom: 24 }}
      title="Pracownik"
      extra={
        <Space>
          <Button
            type="primary"
            htmlType="button"
            onClick={() => setOpen(true)}
          >
            Zarządzaj pracownikami
          </Button>
          {data?.worker?.id && (
            <ModalDelete
              only_icon={true}
              text="przypisanie pracownika od zgłoszenia zbiorczego"
              handleDelete={unassignWorker}
              loading={is_saving_unassign}
            />
          )}
        </Space>
      }
    >
      {is_open && (
        <Modal
          style={{ top: 45 }}
          maskClosable={false}
          width={800}
          title="Zarządzaj pracownikami"
          open={true}
          onCancel={handleCancel}
          footer={[
            <Button key="back" onClick={handleCancel}>
              Anuluj
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={is_saving_bulk_assign}
              onClick={handleAssign}
              disabled={!selectedRowKeys.length || !worker_id}
            >
              Przypisz
            </Button>,
            <Tooltip
              title={
                can_unassign
                  ? ""
                  : "Wszystkie zaznaczone zgłoszenia muszą posiadać pracownika!"
              }
            >
              <Button
                type="primary"
                danger
                disabled={!selectedRowKeys.length || !can_unassign}
                loading={is_saving_bulk_unassign}
                onClick={handleUnassign}
              >
                Odpisz
              </Button>
            </Tooltip>,
          ]}
        >
          <Alert
            className="m-t-10 m-b-20 ant-alert-default"
            description={
              <>
                Wybierz pracownika, aby przypisać go do zgłoszeń zaznaczonych w
                poniższej tabeli. Jeśli wybrane zgłoszenie posiada już
                przypisanego pracownika, zostanie ono zaktualizowane o wybranego
                poniżej. <br />
                <br />{" "}
                <strong>
                  Osoba przypisana do zgłoszenia zbiorczego głównego jest
                  kierownikiem.
                </strong>
              </>
            }
          />
          <Select
            style={{ minWidth: "200px" }}
            size="large"
            placeholder="Wybierz pracownika"
            optionFilterProp="children"
            onChange={(val) => {
              setWorkerId(val);
              setOpen(true);
            }}
            options={workers_list_options}
          />
          <div style={{ padding: "20px 0" }}>
            <Table
              bordered
              columns={columns}
              dataSource={sub_issues}
              size="small"
              rowSelection={rowSelection}
              pagination={false}
            />
          </div>
        </Modal>
      )}
      {data?.worker?.id ? (
        <WorkerAvatar
          worker={data.worker}
          assigned_at={data.assigned_at}
          master_worker
        />
      ) : (
        <Typography.Text>Brak przypisanego pracownika</Typography.Text>
      )}
    </Card>
  );
};

export default ManageWorkers;
